// Generat automat din README.md - Export Utilities pentru toate modulele
import React, { useState } from 'react';
// import * as XLSX from 'xlsx'; // Commented out until XLSX is installed

/**
 * Export data to CSV format
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without extension)
 * @param {Array} columns - Optional: specific columns to export
 */
export const exportToCSV = (data, filename, columns = null) => {
  try {
    let csvContent = '';
    
    if (data.length === 0) {
      throw new Error('Nu există date pentru export');
    }

    // Get headers
    const headers = columns || Object.keys(data[0]);
    csvContent = headers.join(',') + '\n';

    // Add data rows
    data.forEach(row => {
      const values = headers.map(header => {
        let value = row[header] || '';
        
        // Handle different data types
        if (typeof value === 'object' && value !== null) {
          value = JSON.stringify(value).replace(/"/g, '""');
        } else if (typeof value === 'string') {
          value = value.replace(/"/g, '""');
        }
        
        // Wrap in quotes if contains comma or newline
        if (value.toString().includes(',') || value.toString().includes('\n')) {
          return `"${value}"`;
        }
        
        return value;
      });
      csvContent += values.join(',') + '\n';
    });

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return { success: true, message: `Export CSV realizat cu succes: ${filename}.csv` };
  } catch (error) {
    return { success: false, message: `Eroare la export CSV: ${error.message}` };
  }
};

/**
 * Export data to Excel format
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without extension)
 * @param {string} sheetName - Name of the Excel sheet
 * @param {Array} columns - Optional: specific columns to export
 */
export const exportToExcel = (data, filename, sheetName = 'Date', columns = null) => {
  try {
    // Temporarily fallback to CSV export until XLSX is properly installed
    return exportToCSV(data, filename, columns);
    
    /* Original XLSX implementation - uncomment when XLSX is installed
    if (data.length === 0) {
      throw new Error('Nu există date pentru export');
    }

    // Prepare data
    const exportData = columns 
      ? data.map(row => {
          const newRow = {};
          columns.forEach(col => {
            newRow[col] = row[col] || '';
          });
          return newRow;
        })
      : data;

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Auto-size columns
    const columnWidths = [];
    const headers = Object.keys(exportData[0]);
    
    headers.forEach((header, index) => {
      const maxLength = Math.max(
        header.length,
        ...exportData.map(row => (row[header] || '').toString().length)
      );
      columnWidths[index] = { width: Math.min(maxLength + 2, 50) };
    });
    
    worksheet['!cols'] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate Excel file and download
    XLSX.writeFile(workbook, `${filename}.xlsx`);
    
    return { success: true, message: `Export Excel realizat cu succes: ${filename}.xlsx` };
    */
  } catch (error) {
    return { success: false, message: `Eroare la export Excel: ${error.message}` };
  }
};

/**
 * Export data to PDF format (simplified - for complex PDFs use jsPDF with additional setup)
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without extension)
 * @param {string} title - Title of the PDF document
 * @param {Array} columns - Optional: specific columns to export
 */
export const exportToPDF = (data, filename, title = 'Export Date', columns = null) => {
  try {
    if (data.length === 0) {
      throw new Error('Nu există date pentru export');
    }

    // Create HTML content for PDF
    const headers = columns || Object.keys(data[0]);
    
    let htmlContent = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; text-align: center; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .export-info { text-align: center; color: #666; margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <div class="export-info">
            Export generat la: ${new Date().toLocaleString('ro-RO')} | 
            Total înregistrări: ${data.length}
          </div>
          <table>
            <thead>
              <tr>
                ${headers.map(header => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${data.map(row => 
                `<tr>
                  ${headers.map(header => {
                    let value = row[header] || '';
                    if (typeof value === 'object' && value !== null) {
                      value = JSON.stringify(value);
                    }
                    return `<td>${value}</td>`;
                  }).join('')}
                </tr>`
              ).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.html`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return { 
      success: true, 
      message: `Export HTML realizat cu succes: ${filename}.html (poate fi deschis în browser și salvat ca PDF)` 
    };
  } catch (error) {
    return { success: false, message: `Eroare la export PDF: ${error.message}` };
  }
};

/**
 * Smart column mapping pentru export
 * Convertește keyurile din obiect în nume citibile
 */
export const getDisplayColumns = (data, moduleType = 'general') => {
  if (!data || data.length === 0) return [];

  const columnMappings = {
    customers: {
      id: 'ID Client',
      name: 'Nume Client',
      contactPerson: 'Persoană Contact',
      email: 'Email',
      phone: 'Telefon',
      tier: 'Categorie',
      totalValue: 'Valoare Totală',
      lastContact: 'Ultimul Contact',
      status: 'Status',
      rating: 'Rating'
    },
    employees: {
      id: 'ID Angajat',
      name: 'Nume Complet',
      position: 'Poziție',
      department: 'Departament',
      email: 'Email',
      phone: 'Telefon',
      hireDate: 'Data Angajare',
      salary: 'Salariu',
      status: 'Status'
    },
    invoices: {
      id: 'Număr Factură',
      clientName: 'Client',
      amount: 'Sumă',
      issueDate: 'Data Emitere',
      dueDate: 'Scadență',
      status: 'Status',
      paymentMethod: 'Modalitate Plată'
    },
    products: {
      id: 'ID Produs',
      name: 'Nume Produs',
      category: 'Categorie',
      stock: 'Stoc',
      price: 'Preț',
      supplier: 'Furnizor',
      lastRestocked: 'Ultima Realimentare'
    }
  };

  const mappings = columnMappings[moduleType] || {};
  const originalKeys = Object.keys(data[0]);
  
  return originalKeys.map(key => ({
    key,
    displayName: mappings[key] || key.charAt(0).toUpperCase() + key.slice(1),
    selected: true
  }));
};

/**
 * Export cu configurație avansată
 * @param {Array} data - Data to export
 * @param {Object} config - Export configuration
 */
export const advancedExport = (data, config) => {
  const {
    format = 'csv',
    filename,
    title,
    selectedColumns,
    filters,
    sorting,
    moduleType = 'general'
  } = config;

  try {
    let processedData = [...data];

    // Apply filters
    if (filters) {
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          processedData = processedData.filter(item => 
            item[key] && item[key].toString().toLowerCase().includes(filters[key].toLowerCase())
          );
        }
      });
    }

    // Apply sorting
    if (sorting && sorting.key) {
      processedData.sort((a, b) => {
        const aVal = a[sorting.key] || '';
        const bVal = b[sorting.key] || '';
        
        if (sorting.direction === 'desc') {
          return bVal.toString().localeCompare(aVal.toString());
        }
        return aVal.toString().localeCompare(bVal.toString());
      });
    }

    // Filter columns
    const columnsToExport = selectedColumns || Object.keys(data[0]);

    const finalFilename = filename || `export-${moduleType}-${Date.now()}`;

    switch (format.toLowerCase()) {
      case 'csv':
        return exportToCSV(processedData, finalFilename, columnsToExport);
      case 'excel':
        return exportToExcel(processedData, finalFilename, title || 'Date Export', columnsToExport);
      case 'pdf':
        return exportToPDF(processedData, finalFilename, title || 'Export Date', columnsToExport);
      default:
        throw new Error('Format de export neacceptat');
    }
  } catch (error) {
    return { success: false, message: `Eroare la export avansat: ${error.message}` };
  }
};

/**
 * Hook pentru gestionarea exportului
 */
export const useExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const handleExport = async (data, config, onSuccess, onError) => {
    setIsExporting(true);
    setExportProgress(0);

    try {
      // Simulate progress for user feedback
      const progressInterval = setInterval(() => {
        setExportProgress(prev => Math.min(prev + 20, 90));
      }, 100);

      const result = await new Promise((resolve) => {
        setTimeout(() => {
          clearInterval(progressInterval);
          setExportProgress(100);
          resolve(advancedExport(data, config));
        }, 500);
      });

      if (result.success) {
        onSuccess?.(result.message);
      } else {
        onError?.(result.message);
      }

      return result;
    } catch (error) {
      onError?.(`Eroare la export: ${error.message}`);
      return { success: false, message: error.message };
    } finally {
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
      }, 1000);
    }
  };

  return {
    isExporting,
    exportProgress,
    handleExport
  };
};